import { LuTriangleAlert } from 'react-icons/lu';

import { PortfolioEngineAccount } from '@ndvr/portfolio-api';

import { BatchInvestmentRequestItem } from '@/cip/_openapi/portfolio-optimizer';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from 'utils/number-formatter';
import { formatAccountType, titleCase } from 'utils/string-utils';

import { AccountAssessmentBadges } from './AssessmentBadge';

interface Props {
    accounts: PortfolioEngineAccount[];
    warnings?: Record<string, string>;
    isPending: boolean;
    onConfirm: (items: BatchInvestmentRequestItem[]) => void;
    onClose: () => void;
}

const assessmentDescription = (account: PortfolioEngineAccount): string => {
    const flags: string[] = [];
    if (account.hasIdleCash) flags.push('Idle cash');
    if (account.hasInsufficientCash) flags.push('Insufficient cash');
    if (account.hasTaxLossHarvesting) flags.push('Tax loss harvesting');
    return flags.length ? flags.join(', ') : 'Stable';
};

export const TradeRequestConfirmDialog = ({ accounts, warnings = {}, isPending, onConfirm, onClose }: Props) => {
    const hasWarnings = Object.keys(warnings).length > 0;
    const handleSubmit = () => {
        const items: BatchInvestmentRequestItem[] = accounts.map((account) => ({
            portfolioGuid: account.accountGUID,
            description: assessmentDescription(account),
        }));
        onConfirm(items);
    };

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open && !isPending) onClose();
            }}
        >
            <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Trade Request</DialogTitle>
                </DialogHeader>
                <p className="text-cocoa-700 text-sm">
                    {hasWarnings
                        ? 'Some trade requests could not be submitted. Review the warnings below.'
                        : accounts.length === 1
                          ? 'Submit a trade request for the selected account?'
                          : `Submit trade requests for the ${accounts.length} selected accounts?`}
                </p>
                <div className="min-h-0 flex-1 overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Account</TableHead>
                                <TableHead>Target Allocation</TableHead>
                                <TableHead className="text-right">Value</TableHead>
                                <TableHead className="text-right">Cash</TableHead>
                                <TableHead>{hasWarnings ? 'Status' : 'Assessment'}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {accounts.map((account) => {
                                const subParts = [
                                    account.custodyAccountId,
                                    account.custodyType ? titleCase(account.custodyType) : null,
                                    formatAccountType(account.accountType),
                                ].filter(Boolean);
                                const allocation =
                                    account.portfolioAllocationName ?? account.accountAllocationName ?? '—';
                                return (
                                    <TableRow key={account.accountGUID}>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="text-cocoa-1000 font-medium">
                                                    {account.accountNamePlain ?? '—'}
                                                </span>
                                                {subParts.length > 0 && (
                                                    <span className="text-cocoa-500 text-xs">
                                                        {subParts.join(' · ')}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>{allocation}</TableCell>
                                        <TableCell className="text-right whitespace-nowrap">
                                            {formatCurrency(account.aum)}
                                        </TableCell>
                                        <TableCell className="text-right whitespace-nowrap">
                                            {formatCurrency(account.cash)}
                                        </TableCell>
                                        <TableCell>
                                            {warnings[account.accountGUID] ? (
                                                <div className="flex items-start gap-1.5 text-amber-700">
                                                    <LuTriangleAlert
                                                        className="mt-0.5 h-3.5 w-3.5 shrink-0"
                                                        aria-hidden
                                                    />
                                                    <span className="text-xs">{warnings[account.accountGUID]}</span>
                                                </div>
                                            ) : hasWarnings ? (
                                                <span className="text-cocoa-500 text-xs">Submitted</span>
                                            ) : (
                                                <AccountAssessmentBadges account={account} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
                <DialogFooter>
                    {hasWarnings ? (
                        <Button onClick={onClose}>Close</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={onClose} disabled={isPending}>
                                Cancel
                            </Button>
                            <Button onClick={handleSubmit} disabled={isPending || accounts.length === 0}>
                                {isPending ? 'Submitting…' : 'Submit'}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
